import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import Qs from 'qs';
import Article from './Article';
import Filter from './Filter';
import LoadingBar from './LoadingBar';

const UpcomingEvent = ({ kicker, link, title, date, partner }) => {
  return (
    <div className="upcoming-event">
      {kicker && <span className="kicker">{kicker}</span>}
      <h3>{title}</h3>
      <span className="date">{date}</span>
      <span className="partner">{partner}</span>
      <a href={link} className="theme-btn-white">
        Learn More
      </a>
    </div>
  );
};

const InsightsScroller = ({
  insights: initialInsights,
  filters,
  ajaxurl,
  numPages: initialNumPages,
  upcomingEvent
}) => {
  const firstRender = useRef(true);
  const [fetching, setFetching] = useState(false);
  const [query, setQuery] = useState({});
  const [insights, setInsights] = useState(initialInsights);
  const [page, setPage] = useState(0);
  const [numPages, setNumPages] = useState(initialNumPages - 1);
  const [nextPage, setNextPage] = useState(false);

  const { topics, types, authors } = filters;
  const [topic, setTopic] = useState(null);
  const [type, setType] = useState(null);
  const [author, setAuthor] = useState(null);

  const topicHandler = useCallback(
    (id, current) => {
      if (current !== id && !fetching) {
        setTopic(id);
        setType(null);
        setAuthor(null);
        setPage(0);
      }
    },
    [fetching, setTopic, setType, setAuthor]
  );

  const typeHandler = useCallback(
    (id, current) => {
      if (current !== id && !fetching) {
        setType(id);
        setTopic(null);
        setAuthor(null);
        setPage(0);
      }
    },
    [fetching, setTopic, setType, setAuthor]
  );

  const authorHandler = useCallback(
    (id, current) => {
      if (current !== id && !fetching) {
        setAuthor(id);
        setTopic(null);
        setType(null);
        setPage(0);
      }
    },
    [fetching, setTopic, setType, setAuthor]
  );

  useEffect(() => {
    const newQuery = {};
    if (topic) {
      newQuery['tax_query'] = [
        { taxonomy: 'insights_topic', field: 'term_id', terms: topic }
      ];
    }
    if (type) {
      newQuery['tax_query'] = [
        { taxonomy: 'insights_category', field: 'term_id', terms: type }
      ];
    }
    if (author) {
      newQuery['meta_query'] = [
        {
          key: 'insight_author_repeater_',
          value: author,
          compare: '='
        }
      ];
    }
    if (page) {
      newQuery['paged'] = page + 1;
    }
    setQuery(newQuery);
  }, [topic, type, author, page]);

  // Trigger Fetch
  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    setFetching(true);

    if (document.querySelector('.insights-scroller') && page === 0) {
      document
        .querySelector('.insights-scroller')
        .scrollIntoView({ behavior: 'smooth' });
    }

    axios
      .post(ajaxurl, Qs.stringify({ ...query, action: 'insight_scroller' }))
      .then(res => {
        const { insights: newInsights, numPages } = res.data;
        if (page === 0) {
          setInsights(newInsights);
        } else {
          setInsights([...insights, ...newInsights]);
        }
        setNumPages(numPages - 1);
        setFetching(false);
      })
      .catch(function(error) {
        console.log(error);
      });
  }, [JSON.stringify(query)]);

  const nextPageListener = useCallback(
    e => {
      const triggerNextPage =
        document
          .querySelector('article.insight:last-child')
          .getBoundingClientRect().top <
        window.innerHeight / 2;

      if (triggerNextPage && !nextPage) {
        setNextPage(true);
      } else if (!triggerNextPage && nextPage) {
        setNextPage(false);
      }
    },
    [nextPage]
  );

  useEffect(() => {
    if (fetching) return;
    if (nextPage && page < numPages) {
      setPage(page + 1);
    }
  }, [nextPage]);

  // Bind Next Page Listener
  useEffect(() => {
    window.addEventListener('scroll', nextPageListener);
    return () => window.removeEventListener('scroll', nextPageListener);
  }, [nextPageListener]);

  return (
    <>
      <div className="container">
        <h2>LATEST INSIGHTS</h2>
        <div className="insights-scroll__grid">
          <div className="left-column">
            <div className="filters-column">
              <Filter
                title="Topic"
                defaultValue="Latest"
                options={topics}
                current={topic}
                handler={topicHandler}
              />
              <Filter
                title="Type"
                defaultValue="All"
                options={types}
                current={type}
                handler={typeHandler}
              />
              <Filter
                title="Author"
                defaultValue="All"
                options={authors}
                current={author}
                handler={authorHandler}
              />
            </div>
          </div>
          <div
            className={`center-column ${fetching && 'fetching'} ${page > 0 &&
              'next-page'}`}
          >
            {insights.slice(0, 5).map(insight => (
              <Article {...insight} key={insight.title} />
            ))}
            {upcomingEvent && !author && !topic && !type && (
              <UpcomingEvent {...upcomingEvent} />
            )}
            {insights.slice(5).map(insight => (
              <Article {...insight} key={insight.title} />
            ))}
          </div>
          <div className="right-column"></div>
        </div>
      </div>
      {(page < numPages || fetching) && (
        <LoadingBar loading={fetching && page > 0} />
      )}
    </>
  );
};

export default InsightsScroller;
